import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Share2, Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Footer() {
  const [email, setEmail] = useState('');

  const handleNewsletter = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      toast.success('Thank you for subscribing to the Royal Pet Store newsletter!');
      setEmail('');
    }
  };

  return (
    <footer className="bg-primary-900 text-gray-400 mt-20 relative overflow-hidden">
      {/* Top divider gradient accent */}
      <div className="h-[3px] bg-gradient-to-r from-accent/10 via-accent to-accent/10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">

          {/* Brand & Contact */}
          <div className="space-y-6 sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-3.5 group">
              <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 transition-transform duration-500 group-hover:rotate-12 border border-gray-800 bg-white">
                <img src="/logo.jpeg" alt="Royal Pet Store Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <span className="block text-[20px] font-serif font-black tracking-tight text-white">Royal Pet Store</span>
                <span className="block text-[8px] font-bold tracking-[0.3em] text-accent uppercase -mt-0.5">Premium Pet Boutique</span>
              </div>
            </Link>
            <p className="text-xs leading-relaxed text-gray-500 max-w-sm">
              Connecting families with vet-certified pets, organic feed, and premium accessories. Providing the ultimate standard in animal care and safety.
            </p>
            <div className="space-y-3.5 text-xs">
              <a href="tel:03295025172" className="flex items-center gap-3 hover:text-white transition-colors group">
                <div className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/15 transition-colors">
                  <Phone size={13} className="text-gray-500 group-hover:text-accent transition-colors" />
                </div>
                <span>0329 5025172</span>
              </a>
              <a href="mailto:info@royalpetstore.store" className="flex items-center gap-3 hover:text-white transition-colors group">
                <div className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/15 transition-colors">
                  <Mail size={13} className="text-gray-500 group-hover:text-accent transition-colors" />
                </div>
                <span>info@royalpetstore.store</span>
              </a>
              <a href="https://wa.me/923295025172" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white transition-colors group">
                <div className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/15 transition-colors">
                  <Share2 size={13} className="text-gray-500 group-hover:text-accent transition-colors" />
                </div>
                <span>WhatsApp Live Chat</span>
              </a>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin size={13} className="text-gray-500" />
                </div>
                <span className="leading-relaxed">Royal Pet Store, Bakra mandi dhok syedan road kamalabad Rawalpindi</span>
              </div>
            </div>
          </div>

          {/* Shop Links */}
          <div className="lg:pl-6">
            <h3 className="text-white text-[11px] font-bold tracking-[0.2em] uppercase mb-6 relative pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-8 after:h-0.5 after:bg-accent">Shop Departments</h3>
            <ul className="space-y-3">
              {[
                { to: '/new-in', label: 'New Arrivals' },
                { to: '/category/pets', label: 'Adoptable Pets' },
                { to: '/category/pet-foods', label: 'Pet Feeds & Diet' },
                { to: '/category/pet-accessories', label: 'Toys & Accessories' },
                { to: '/search', label: 'Explore Catalogue' },
              ].map(item => (
                <li key={item.to}>
                  <Link to={item.to} className="text-xs hover:text-white hover:translate-x-1 transition-all inline-block">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Info Links */}
          <div>
            <h3 className="text-white text-[11px] font-bold tracking-[0.2em] uppercase mb-6 relative pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-8 after:h-0.5 after:bg-accent">Information</h3>
            <ul className="space-y-3">
              {[
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Support' },
                { to: '/adoption-guide', label: 'Pet Adoption Guide' },
                { to: '/pet-care', label: 'Pet Care Articles' },
                { to: '/faqs', label: 'Common FAQs' },
                { to: '/refund-policy', label: 'Store Policies' },
                { to: '/shipping-policy', label: 'Climate Transit Info' },
              ].map(item => (
                <li key={item.to}>
                  <Link to={item.to} className="text-xs hover:text-white hover:translate-x-1 transition-all inline-block">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter / CTA */}
          <div className="space-y-5">
            <h3 className="text-white text-[11px] font-bold tracking-[0.2em] uppercase mb-6 relative pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-8 after:h-0.5 after:bg-accent">Stay Updated</h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              Subscribe to receive updates on vet clearances, new adoptable breeds, and promotional feed offers.
            </p>
            <form onSubmit={handleNewsletter} className="space-y-2.5">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="w-full bg-[#2A1E17] border border-white/5 px-4 py-3 text-xs text-white placeholder-gray-650 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent rounded-xl transition-all"
              />
              <button
                type="submit"
                className="w-full bg-accent hover:bg-accent/80 text-white py-3 text-[10px] font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2 rounded-xl shadow-md"
              >
                Join Newsletter <ArrowRight size={13} />
              </button>
            </form>
            <div className="flex gap-3 pt-3">
              <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-xl border border-gray-800 bg-[#1E1510] flex items-center justify-center hover:border-accent hover:text-accent transition-all">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4.5 h-4.5"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
              </a>
              <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-xl border border-gray-800 bg-[#1E1510] flex items-center justify-center hover:border-pink-500 hover:text-pink-400 transition-all">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>
              </a>
              <a href="https://wa.me/923295025172" aria-label="WhatsApp" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-xl border border-gray-800 bg-[#1E1510] flex items-center justify-center hover:border-green-500 hover:text-green-400 transition-all">
                <Phone size={17} />
              </a>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5 py-6 px-4 bg-[#140E0A]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[10px] text-gray-600">© {new Date().getFullYear()} Royal Pet Store. All Rights Reserved.</p>
          <p className="text-[10px] text-gray-500 flex flex-wrap gap-x-4 gap-y-1 justify-center">
            <span>Verified Breeder Network</span>
            <span className="text-gray-700">|</span>
            <span>Vet Inspection Clearance</span>
            <span className="text-gray-700">|</span>
            <span>Safe Temperature Transit</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
